// The live-call card's words (WEB-07): shown while a call on her phone rings or goes on, naming the customer as her
// phone did (D-094). Nothing here opens a page: the card offers one click, and only her click opens the customer.
import { fmtPhone } from "./format";
import type { LiveCall } from "./types";

/** A call still going after this long lost its end (the phone was offline when it ended): no card. */
export const LIVE_CALL_LONGEST_MS = 2 * 60 * 60_000;
/** Nobody lets a phone ring this long: a ring with no answer or end since lost its end too. */
export const RING_LONGEST_MS = 3 * 60_000;

export interface LiveCallView {
  /** One per call: hiding the card hides it for this call only, ringing or answered. */
  key: string;
  /** "Incoming call · 0:12", "On a call · 2:05". */
  eyebrow: string;
  name: string;
  /** Whose customer, the number, what they want, their last conversation: what the phone knew. */
  lines: string[];
  /** Only when she may open the customer. */
  leadId: string | null;
}

/** What the card shows now, or null: no call going on, nothing to name, or a call whose end was lost. */
export function liveCallView(call: LiveCall | null, now: Date): LiveCallView | null {
  if (!call || call.state === "ended" || !call.started_at) return null;
  const ms = now.getTime() - new Date(call.started_at).getTime();
  if (ms > (call.state === "ringing" ? RING_LONGEST_MS : LIVE_CALL_LONGEST_MS)) return null;
  const card = call.card;
  const eyebrow = `${call.state === "ringing" ? "Incoming call" : "On a call"} · ${clock(Math.max(0, ms))}`;
  const view = (name: string, lines: (string | null | undefined)[]): LiveCallView => ({
    key: `call@${call.started_at}`,
    eyebrow,
    name,
    lines: lines.filter((l): l is string => !!l),
    leadId: call.lead?.id ?? null,
  });
  if (!card) return call.lead ? view(call.lead.name, []) : null;
  switch (card.kind) {
    case "mine":
      return view(call.lead?.name ?? card.name ?? fmtPhone(call.phone), [card.who ?? card.context, card.conversation]);
    case "colleague":
      return view(card.name ?? fmtPhone(call.phone), [
        card.owner ? `${card.owner}'s customer` : "Not assigned to anyone yet",
        card.who ?? card.context,
        card.conversation,
      ]);
    case "colleague_hidden":
      return view("A colleague's customer", [fmtPhone(call.phone)]);
    case "new":
      return view("New number", [fmtPhone(call.phone), "Not in your company's records"]);
  }
}

/** 0:42, 12:05, 1:02:09. */
export function clock(ms: number): string {
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}
